import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Coins, Plus, Loader2, Sparkles, Zap, Crown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useWorkspace } from '@/context/WorkspaceContext';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const PACKAGES = [
  { id: 'starter', name: 'Starter', credits: 100, price: 499, icon: Sparkles, popular: false },
  { id: 'growth', name: 'Growth', credits: 300, price: 999, icon: Zap, popular: true },
  { id: 'pro', name: 'Pro', credits: 1000, price: 2499, icon: Crown, popular: false }
];

// Routes where credit enforcement should NOT apply
const EXEMPT_ROUTES = [
  '/',           // Landing page
  '/get-started', // Get started page
  '/shared/',    // Shared design pages (prefix)
  '/join/',      // Join workspace pages (prefix)
  '/admin/',     // Admin pages (admin/team don't need credits)
];

// Admin email - exempt from credit enforcement
const ADMIN_EMAIL = 'pgundepudi@gmail.com';

/**
 * CreditEnforcementOverlay - Blocks all app activity when credits reach 0
 * Similar to Emergent's credit enforcement behavior
 * Admin and team members are exempt
 */
const CreditEnforcementOverlay = () => {
  const location = useLocation();
  const { credits, workspace, loading, isOwner, refreshCredits, user } = useWorkspace();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isAdminOrTeam, setIsAdminOrTeam] = useState(false);

  // Check if user is admin or team member
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.email) return;
      
      // Admin is always exempt
      if (user.email === ADMIN_EMAIL) {
        setIsAdminOrTeam(true);
        return;
      }
      
      // Check if user is a team member of admin
      try {
        const response = await axios.get(`${API}/auth/check-team-status`, { withCredentials: true });
        setIsAdminOrTeam(response.data.is_team_member || false);
      } catch (err) {
        setIsAdminOrTeam(false);
      }
    };
    
    checkAdminStatus();
  }, [user?.email]);

  // Check if current route is exempt from credit enforcement
  const isExemptRoute = EXEMPT_ROUTES.some(route => {
    if (route.endsWith('/')) {
      return location.pathname.startsWith(route) || location.pathname === route.slice(0, -1);
    }
    return location.pathname === route;
  });

  // Don't show overlay if:
  // - On an exempt route (public pages)
  // - Still loading workspace data
  // - User is not logged in (no workspace)
  // - User has credits remaining
  // - User is admin or team member
  if (isExemptRoute || loading || !workspace || credits > 0 || isAdminOrTeam) {
    return null;
  }

  const handleTopUp = async (pkg) => {
    setSelectedPackage(pkg.id);
    setProcessing(true);

    try {
      // Create order
      const orderResponse = await axios.post(
        `${API}/payments/create-order`,
        { package: pkg.id },
        { withCredentials: true }
      );

      const { order_id, amount, key_id } = orderResponse.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'Customer Design Studio',
        description: `${pkg.name} Package - ${pkg.credits} Credits`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(
              `${API}/payments/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.success(`${pkg.credits} credits added! You can now continue.`);
            await refreshCredits();
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {},
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            setSelectedPackage(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setProcessing(false);
      setSelectedPackage(null);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="credit-enforcement-overlay"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header - Warning Banner */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">Credits Depleted</h2>
              <p className="text-white/90">
                Your workspace has run out of credits. Add credits to continue using AI Design Studio.
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Current Balance */}
          <div className="flex items-center justify-center gap-3 mb-6 py-4 bg-red-50 rounded-xl border border-red-200">
            <Coins className="w-6 h-6 text-red-500" />
            <span className="text-lg font-semibold text-red-700">Current Balance: {credits} credits</span>
          </div>

          {/* Message for non-owners */}
          {!isOwner && (
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-amber-800 text-center">
                <strong>Note:</strong> Only workspace owners can purchase credits. 
                Please contact your workspace owner to top up credits.
              </p>
            </div>
          )}

          {/* Packages - Only show for owners */}
          {isOwner && (
            <>
              <h3 className="text-lg font-semibold text-zinc-900 mb-4 text-center">
                Choose a package to continue
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {PACKAGES.map((pkg) => {
                  const Icon = pkg.icon;
                  const isSelected = selectedPackage === pkg.id;
                  
                  return (
                    <div
                      key={pkg.id}
                      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${
                        pkg.popular 
                          ? 'border-indigo-500 bg-indigo-50 shadow-lg shadow-indigo-100' 
                          : 'border-zinc-200 hover:border-indigo-300 hover:bg-zinc-50'
                      }`}
                      onClick={() => !processing && handleTopUp(pkg)}
                      data-testid={`credit-package-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                          Best Value
                        </div>
                      )}
                      
                      <div className="text-center">
                        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                          pkg.popular ? 'bg-indigo-500' : 'bg-zinc-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${pkg.popular ? 'text-white' : 'text-zinc-600'}`} />
                        </div>
                        
                        <h4 className="font-bold text-lg text-zinc-900 mb-1">{pkg.name}</h4>
                        <p className="text-2xl font-bold text-zinc-900 mb-1">
                          ₹{pkg.price}
                        </p>
                        <p className="text-sm text-zinc-500 mb-2">{pkg.credits} credits</p>
                        <p className="text-xs text-indigo-600 font-medium">
                          ₹{(pkg.price / pkg.credits).toFixed(2)}/credit
                        </p>
                      </div>

                      {isSelected && processing && (
                        <div className="absolute inset-0 bg-white/90 rounded-xl flex items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* What credits are used for */}
              <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
                <p className="text-sm text-zinc-600 text-center mb-3 font-medium">What credits are used for:</p>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    AI Design Generation: 20 credits
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    Design Revision: 5 credits
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Add Page: 5 credits
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    Export: 2 credits
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Support link */}
          <p className="text-center text-sm text-zinc-500 mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:support@wowcx.io" className="text-indigo-600 hover:underline">
              support@wowcx.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditEnforcementOverlay;
